import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { localeUI } from '../lib/format';
import { Folder, Mail, MessageSquare, Paperclip, Phone, Search, Settings } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ClaimDrawer } from './ClaimDrawer';

/**
 * ⌘K — BUSCAR DESDE DONDE SEA, DENTRO DE TODO.
 *
 * El Finder ya encontraba el EXPEDIENTE (nombre, dirección, claim #, monto). Lo que faltaba era
 * encontrar el DATO: *"¿dónde estaba lo del ajustador que dijo que iba a re-inspeccionar?"*. Eso
 * vive dentro de una nota, un email, un SMS o la transcripción de una llamada, y hasta hoy solo se
 * podía encontrar si uno se acordaba de qué cliente era. Si no te acordás, no existía.
 *
 * Dos consultas en paralelo: claims (el expediente) y contenido (el dato). Se abren en el mismo
 * drawer de siempre — un resultado de contenido entra directo por la pestaña Historia, donde el
 * hecho está en su contexto y no suelto.
 */
type Claim = { id: string; claim_number: string | null; homeowner_name: string | null; address: string | null; current_status: string | null };
type Hit = {
  id: string; tipo: 'nota' | 'monday' | 'email' | 'sms' | 'llamada' | 'archivo';
  claimId: string | null; claimNumber: string | null; homeowner: string | null;
  titulo: string; extracto: string | null; at: string | null; autor: string | null;
};

const ICO: Record<Hit['tipo'], ReactNode> = {
  nota: <MessageSquare size={13} strokeWidth={1.75} />,
  monday: <Settings size={13} strokeWidth={1.75} />,
  email: <Mail size={13} strokeWidth={1.75} />,
  sms: <MessageSquare size={13} strokeWidth={1.75} />,
  llamada: <Phone size={13} strokeWidth={1.75} />,
  archivo: <Paperclip size={13} strokeWidth={1.75} />,
};

export function CommandSearch() {
  const { t } = useI18n();
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const [claims, setClaims] = useState<Claim[]>([]);
  const [hits, setHits] = useState<Hit[]>([]);
  const [cargando, setCargando] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [sel, setSel] = useState<{ id: string; tab?: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl+K desde cualquier pantalla. Esc cierra.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAbierto((v) => !v);
      } else if (e.key === 'Escape') setAbierto(false);
    };
    // El chip ⌘K del sidebar también lo abre (rediseño TP: buscador pill del menú).
    const onOpen = () => setAbierto(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('cmdk:open', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('cmdk:open', onOpen); };
  }, []);

  useEffect(() => { if (abierto) setTimeout(() => inputRef.current?.focus(), 30); }, [abierto]);

  // Debounce de 280ms: escribir "reinspection" son 12 teclas, y sin esto son 12 búsquedas
  // completas sobre 62k textos. Con la pausa es UNA.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setClaims([]); setHits([]); setCargando(false); return; }
    setCargando(true);
    // `vivo` se apaga desde la limpieza del EFECTO (antes se devolvía desde el callback del
    // timeout, donde nadie lo leía): "rei" ya no puede pisar a "reins".
    let vivo = true;
    const id = window.setTimeout(() => {
      Promise.all([
        api.get<{ claims: Claim[] }>(`/api/search?q=${encodeURIComponent(term)}`).catch(() => ({ claims: [] })),
        term.length >= 3
          ? api.get<{ hits: Hit[] }>(`/api/search/deep?q=${encodeURIComponent(term)}`).catch(() => ({ hits: [] }))
          : Promise.resolve({ hits: [] as Hit[] }),
      ]).then(([a, b]) => {
        if (!vivo) return;
        setClaims((a.claims ?? []).slice(0, 8));
        setHits(b.hits ?? []);
        setCursor(0);
        setCargando(false);
      });
    }, 280);
    return () => { vivo = false; window.clearTimeout(id); };
  }, [q]);

  // Una sola lista plana para que las flechas atraviesen los dos grupos sin saltos raros.
  const filas = useMemo(
    () => [
      ...claims.map((c) => ({ clase: 'claim' as const, c })),
      ...hits.map((h) => ({ clase: 'hit' as const, h })),
    ],
    [claims, hits],
  );

  const abrir = (i: number) => {
    const f = filas[i];
    if (!f) return;
    if (f.clase === 'claim') setSel({ id: f.c.id });
    else if (f.h.claimId) setSel({ id: f.h.claimId, tab: 'historia' });
    else return;
    setAbierto(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((n) => Math.min(n + 1, filas.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((n) => Math.max(n - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); abrir(cursor); }
  };

  return (
    <>
      {abierto && (
        <div onClick={() => setAbierto(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(15,23,42,.45)',
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '8vh' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(720px, 94vw)', maxHeight: '78vh', display: 'flex', flexDirection: 'column',
              background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 14,
              boxShadow: '0 24px 60px rgba(0,0,0,.28)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
              <span style={{ display: 'inline-flex', color: 'var(--muted)' }}><Search size={15} strokeWidth={1.75} /></span>
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
                placeholder={t('ck.placeholder')}
                style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 'var(--fs-base)', color: 'var(--ink)' }} />
              {cargando && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>…</span>}
              <kbd className="muted" style={{ fontSize: 'var(--fs-2xs)', border: '1px solid var(--line)', borderRadius: 4, padding: '1px 5px' }}>esc</kbd>
            </div>

            <div style={{ overflowY: 'auto' }}>
              {q.trim().length < 2 ? (
                <div className="muted" style={{ padding: 18, fontSize: 'var(--fs-xs)', lineHeight: 1.7 }}>
                  {t('ck.hint')}
                </div>
              ) : !filas.length && !cargando ? (
                <div className="muted" style={{ padding: 18, fontSize: 'var(--fs-sm)' }}>{t('ck.none')}</div>
              ) : (
                <>
                  {claims.length > 0 && <Rotulo texto={t('ck.claims')} n={claims.length} />}
                  {claims.map((c, i) => (
                    <Fila key={c.id} activo={cursor === i} onHover={() => setCursor(i)} onClick={() => abrir(i)}>
                      <span style={{ display: 'inline-flex', color: 'var(--muted)' }}><Folder size={13} strokeWidth={1.75} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>
                          {c.homeowner_name || '—'} <span className="muted" style={{ fontWeight: 'var(--fw-body)', fontFamily: 'var(--font-mono)' }}>#{c.claim_number || '—'}</span>
                        </div>
                        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.address || ''}
                        </div>
                      </div>
                      {c.current_status && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{c.current_status}</span>}
                    </Fila>
                  ))}

                  {hits.length > 0 && <Rotulo texto={t('ck.inside')} n={hits.length} />}
                  {hits.map((h, k) => {
                    const i = claims.length + k;
                    return (
                      <Fila key={h.id} activo={cursor === i} onHover={() => setCursor(i)} onClick={() => abrir(i)}>
                        <span style={{ display: 'inline-flex', color: 'var(--muted)' }}>{ICO[h.tipo] ?? '•'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 'var(--fs-xs)', display: 'flex', gap: 6, alignItems: 'baseline' }}>
                            <b>{h.homeowner || h.titulo}</b>
                            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                              {h.claimNumber ? `#${h.claimNumber}` : ''} {h.autor ? `· ${h.autor}` : ''}
                              {h.at ? ` · ${new Date(h.at).toLocaleDateString(localeUI())}` : ''}
                            </span>
                          </div>
                          {/* El extracto viene recortado ALREDEDOR del match desde el backend. */}
                          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Resaltado texto={h.extracto ?? ''} q={q.trim()} />
                          </div>
                        </div>
                      </Fila>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {sel && <ClaimDrawer claimId={sel.id} initialTab={sel.tab} onClose={() => setSel(null)} showChat />}
    </>
  );
}

function Rotulo({ texto, n }: { texto: string; n: number }) {
  return (
    <div className="muted" style={{ padding: '8px 14px 3px', fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {texto} · {n}
    </div>
  );
}

function Fila({ activo, onHover, onClick, children }: {
  activo: boolean; onHover: () => void; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <div onMouseEnter={onHover} onClick={onClick}
      style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '7px 14px', cursor: 'pointer',
        background: activo ? 'var(--surface-2)' : 'transparent' }}>
      {children}
    </div>
  );
}

/** Marca el término dentro del extracto: sin esto hay que releer la línea para ver por qué salió. */
function Resaltado({ texto, q }: { texto: string; q: string }) {
  if (!q) return <>{texto}</>;
  const i = texto.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{texto}</>;
  return (
    <>
      {texto.slice(0, i)}
      <mark style={{ background: 'var(--accent-soft, #fde68a)', color: 'inherit', padding: '0 1px', borderRadius: 2 }}>
        {texto.slice(i, i + q.length)}
      </mark>
      {texto.slice(i + q.length)}
    </>
  );
}
